import Skeleton from '@/common/components/atom/skeleton/skeleton';
import classNames from '@/common/utils/classNames';
import type { SectionId } from '@/modules/profile/utils/sectionReadiness';
import StatusIcon from '@/common/components/icons/status';
import PresenceIcon from '@/common/components/icons/presence';
import { createContext, useContext, type ReactElement, type ReactNode } from 'react';

// Rendered height (px) of each section *when present*, at 412px width. Median of real profiles from
// /s/tehran/internal/ (production build at https://local.paziresh24.com): 2026-09-28 (task-5-report.md),
// booking/centers/gallery/seo re-measured 2026-09-29 on 4 doctors (polish-report.md). Re-measure if a
// section's design changes.
export const SECTION_SKELETON_HEIGHT: Record<SectionId, number> = {
  head: 277,
  booking: 427,
  risman: 427,
  about: 3310,
  credentials: 192,
  gallery: 231,
  waiting: 268,
  centers: 663,
  reviews: 2275,
  widgets: 64,
  insurance: 120,
  seo: 194,
  recommend: 44,
};

/**
 * Sections that render nothing (or nothing on mobile) for almost every doctor: Hamdast widget slots,
 * insurance banner, credentials (microservice only), Risman, "recommend other doctors". While pending
 * they reserve no space, so the skeleton column looks like the page most users will actually get
 * (head → booking → address → …). When one does appear, only skeletons below it move — they are
 * remounted on every reveal (`pendingKey`), so this is not a layout shift.
 */
export const COLLAPSED_WHILE_PENDING: ReadonlySet<SectionId> = new Set<SectionId>([
  'widgets',
  'insurance',
  'credentials',
  'risman',
  'recommend',
  // Gallery / Aparat video / online-visit waiting chart: absent for most doctors — a 231–268px
  // placeholder that then disappears pulled the reviews up (seen on desktop).
  'gallery',
  'waiting',
]);

/** One shimmering block (same animation as every other skeleton in the app). */
const B = ({ w, h, r = 'md', className }: { w: string; h: number; r?: 'md' | 'lg' | 'full'; className?: string }) => (
  <Skeleton w={w} h={`${h}px`} rounded={r} className={classNames('shrink-0', className)} />
);

/** White card on the grey page, like the real sections (square on mobile, rounded from md). */
const Card = ({ className, children }: { className?: string; children: ReactNode }) => (
  <div className={classNames('bg-white md:rounded-lg overflow-hidden', className)}>{children}</div>
);

/** Section title that sits on the grey page above its card (h2, 16px / 18px line). */
const OuterTitle = ({ w }: { w: string }) => (
  <div className="px-4 h-[18px] flex">
    <B w={w} h={18} />
  </div>
);

/** Rate summary line: green badge + «رضایت (N نظر)» + info icon — 176×29 in the header. */
export const RatePillSkeleton = ({ className }: { className?: string }) => (
  <div className={classNames('flex items-center gap-1 h-[29px]', className)} aria-hidden="true">
    <B w="62px" h={29} r="full" />
    <B w="89px" h={21} />
    <B w="18px" h={18} r="full" />
  </div>
);

export type HeadSeed = {
  online?: { price?: number } | false;
  presence?: boolean;
  display_name: string;
  image?: string;
  expertiseText?: string;
  keywordTags?: string[];
  view?: string;
  rate?: number;
  ratesCount?: number;
};

const Icon = ({ d, size = 18 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#1b1b18" strokeWidth="2" aria-hidden="true">
    <path d={d} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const EYE = 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z';
const BOOKMARK = 'M6 3h12v18l-6-4-6 4V3Z';
const SHARE = 'M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm12 7a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM8.6 13.5l6.8 4M15.4 6.5l-6.8 4';

/** Same rate line as the real header, from the search result (rates shown only from 5 reviews). */
export const SeedRate = ({ rate, count }: { rate: number; count: number }) => (
  <div className="flex items-center gap-1 h-[29px] self-center text-sm" aria-hidden="true">
    <span className="h-[29px] px-3 rounded-full bg-green-600 text-slate-50 font-medium flex items-center gap-1"><span>{rate.toFixed(1)}</span><span>از ۵</span></span>
    <span className="font-medium text-[#2b2f33]">رضایت ({count} نظر)</span>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-slate-700">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" strokeLinecap="round" />
    </svg>
  </div>
);

/**
 * Profile head card (223px) + tab bar (1px gap, 53px), measured on the real ProfileHead at 412px.
 * Without a seed everything is a placeholder; with a search seed (the navigation preview) the name,
 * photo and chips are the card's own, so the real header replaces it without a visible change.
 */
export const ProfileHeadSkeleton = ({ seed }: { seed?: HeadSeed | null }) => {
  const chips = String(seed?.expertiseText ?? '')
    .split('|')
    .map(x => x.trim())
    .filter(Boolean)
    .concat(seed?.keywordTags ?? []);
  return (
    <div className="flex flex-col gap-px">
      <div className="bg-white rounded-lg p-4 flex flex-col gap-3 h-[223px]">
        {seed ? (
          <div className="flex justify-between items-center h-[21px] text-sm font-normal text-[#2b2f33]">
            <span className="flex items-center gap-1">{seed.view ? <><span>{seed.view}</span><Icon d={EYE} size={18} /></> : null}</span>
            <div className="flex gap-3">
              <span className="flex items-center gap-1"><Icon d={BOOKMARK} size={16} />ذخیره</span>
              <span className="flex items-center gap-1"><Icon d={SHARE} size={16} />اشتراک گذاری</span>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center h-[21px]">
            <B w="51px" h={21} />
            <div className="flex gap-3">
              <B w="57px" h={21} />
              <B w="100px" h={21} />
            </div>
          </div>
        )}
        <div className="bg-slate-50 rounded-lg p-4 flex items-center gap-3 h-[117px]">
          {seed?.image ? (
            // eslint-disable-next-line @next/next/no-img-element -- the card's already-cached URL, as is
            <img
              src={seed.image}
              alt=""
              width={85}
              height={85}
              className="w-[85px] h-[85px] shrink-0 rounded-full border-2 border-slate-200 object-cover"
            />
          ) : (
            <B w="85px" h={85} r="full" />
          )}
          <div className="flex flex-col gap-3 flex-grow min-w-0">
            {seed ? (
              <h2 className="text-xl leading-5 font-bold line-clamp-2">{seed.display_name}</h2>
            ) : (
              <B w="70%" h={20} />
            )}
            {chips.length > 0 ? (
              <div className="flex gap-1 overflow-hidden">
                {chips.map(chip => (
                  <span
                    key={chip}
                    className="shrink-0 whitespace-nowrap h-6 px-3 pt-1 rounded-[14px] border border-slate-200 bg-slate-50 text-xs leading-[18px] font-semibold"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            ) : (
              <div className="flex gap-1">
                <B w="154px" h={24} r="full" />
                <B w="68px" h={24} r="full" />
              </div>
            )}
          </div>
        </div>
        {seed?.rate != null && (seed.ratesCount ?? 0) >= 5 ? (
          <SeedRate rate={seed.rate} count={seed.ratesCount ?? 0} />
        ) : (
          <RatePillSkeleton className="self-center" />
        )}
      </div>
      <div className="bg-white p-4 h-[53px] grid grid-cols-2 text-sm leading-[21px] text-center">
        <span>نظرات</span>
        <span>آدرس و تلفن</span>
      </div>
    </div>
  );
};

const HeadSkeleton = () => <ProfileHeadSkeleton />;

/** Card header row: title (with a leading icon) on the right, a value on the left, 2px divider. */
const CardHeader = ({ icon, title, value }: { icon: ReactNode; title: string; value?: string }) => (
  <div className="flex justify-between items-center px-4 pb-4 border-b-2 border-slate-100">
    <div className="flex items-center gap-2">
      {icon}
      <B w={title} h={21} />
    </div>
    {value && <B w={value} h={21} />}
  </div>
);

/** What the search result already told us about this doctor's booking cards. */
export type BookingHints = { online?: { price?: number } | false; presence?: boolean };
export const BookingHintsContext = createContext<BookingHints | null>(null);

const ONLINE_CARD_H = 229;
const GAP = 12;
const PRESENCE_CARD_H = SECTION_SKELETON_HEIGHT.booking - ONLINE_CARD_H - GAP;

/** Booking skeleton height for these hints (only the cards this doctor has). */
export const bookingSkeletonHeight = (h: BookingHints | null) => {
  const online = h?.online !== false;
  const presence = h?.presence !== false;
  if (online && presence) return SECTION_SKELETON_HEIGHT.booking;
  if (online) return ONLINE_CARD_H;
  if (presence) return PRESENCE_CARD_H;
  return SECTION_SKELETON_HEIGHT.booking;
};

/**
 * Online-visit card (229px) + in-person booking card, 12px apart on the grey page. Driven by the
 * search result when there is one: only the cards this doctor actually has, their real titles and
 * the online-visit price; only what is still unknown shimmers.
 */
export const BookingSkeleton = () => {
  const hints = useContext(BookingHintsContext);
  const online = hints?.online !== false;
  const presence = hints?.presence !== false;
  return (
    <div className="flex flex-col gap-3 h-full">
      {online && (
        <Card className="py-4 h-[229px] shrink-0">
          {hints?.online ? (
            <div className="flex justify-between items-center px-4 pb-4 border-b-2 border-slate-100 h-[39px] text-sm font-bold text-[#2b2f33]">
              <div className="flex items-center gap-2">
                <StatusIcon />
                <span>آنلاین ویزیت شوید</span>
              </div>
              <B w="82px" h={21} />
            </div>
          ) : (
            <CardHeader icon={<B w="18px" h={18} r="full" />} title="108px" value="82px" />
          )}
          <div className="px-4 pt-4 flex flex-col gap-3">
            <div className="border-r-2 border-slate-200 flex flex-col">
              <div className="flex justify-between items-center h-[29px] pr-3">
                <B w="103px" h={21} />
                <B w="139px" h={24} />
              </div>
              <div className="flex items-center h-[29px] pr-3">
                <B w="85%" h={21} />
              </div>
              <div className="flex items-center h-[29px] pr-3">
                <B w="72%" h={21} />
              </div>
            </div>
            <div className="h-[43px] rounded-md bg-blue-100 skeleton-shimmer" />
          </div>
        </Card>
      )}
      {presence && (
        <Card className="py-4 flex-grow">
          {hints ? (
            <div className="flex items-center gap-2 px-4 pb-4 border-b-2 border-slate-100 h-[39px] text-sm font-bold text-[#2b2f33]">
              <PresenceIcon />
              <span>نوبت اینترنتی و مراجعه حضوری</span>
            </div>
          ) : (
            <CardHeader icon={<B w="12px" h={20} />} title="178px" />
          )}
          <div className="mx-4 mt-3 border-r-2 border-slate-200 px-3 py-1 h-8 flex items-center">
            <B w="156px" h={21} />
          </div>
          <div className="px-4 mt-3">
            <div className="h-12 rounded-lg bg-blue-100 skeleton-shimmer" />
          </div>
        </Card>
      )}
    </div>
  );
};

/** «آدرس و تلفن تماس»: title on the page, white card with one slate box per center. */
export const CentersSkeleton = () => (
  <div className="flex flex-col gap-3 h-full">
    <OuterTitle w="124px" />
    <Card className="p-3 flex flex-col gap-3 flex-grow">
      {[0, 1].map(i => (
        <div key={i} className="bg-slate-50 rounded-lg p-4 flex flex-col gap-3 shrink-0">
          <div className="flex flex-col gap-2">
            <B w="70%" h={24} />
            <B w="100%" h={16} />
            <B w="60%" h={16} />
          </div>
          <div className="h-12 rounded-md border border-slate-200 flex items-center justify-center">
            <B w="86px" h={21} />
          </div>
          <div className="h-[45px] rounded-md border border-slate-200 flex items-center justify-center">
            <B w="110px" h={21} />
          </div>
          <div className="h-[45px] rounded-md border border-slate-200 flex items-center justify-center">
            <B w="184px" h={21} />
          </div>
        </div>
      ))}
    </Card>
  </div>
);

const TextLines = ({ lines, lh = 24 }: { lines: number; lh?: number }) => (
  <div className="flex flex-col">
    {Array.from({ length: lines }, (_, i) => (
      <div key={i} className="flex items-center" style={{ height: lh }}>
        <B w={i === lines - 1 ? '60%' : '100%'} h={14} />
      </div>
    ))}
  </div>
);

/** Reviews: summary card (title, rate line, 3 rate bars) + list card (filters, search, reviews). */
const ReviewsSkeleton = () => (
  <div className="flex flex-col gap-px h-full">
    <Card className="h-[268px] shrink-0">
      <div className="px-4 pt-4 h-10">
        <B w="70%" h={24} />
      </div>
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-center justify-center gap-2 h-7">
          <B w="70px" h={28} r="full" />
          <B w="89px" h={20} />
          <B w="18px" h={18} r="full" />
        </div>
        {[0, 1, 2].map(i => (
          <div key={i} className="flex flex-col gap-1 h-11">
            <B w="120px" h={20} />
            <div className="flex items-center gap-3 h-5">
              <B w="100%" h={8} r="full" className="flex-1" />
              <B w="21px" h={20} />
            </div>
          </div>
        ))}
      </div>
    </Card>
    <Card className="flex-grow">
      <div className="p-4 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="h-[42px] rounded-xl border border-slate-200" />
          <div className="h-[42px] rounded-xl border border-slate-200" />
        </div>
        <div className="h-[42px] rounded-xl border border-slate-200" />
      </div>
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="p-4 border-b border-slate-200 flex flex-col gap-3">
          <div className="flex justify-between items-center h-11">
            <div className="flex items-center gap-3">
              <B w="40px" h={40} r="full" />
              <div className="flex flex-col gap-2">
                <B w="110px" h={14} />
                <B w="70px" h={12} />
              </div>
            </div>
            <B w="35px" h={35} />
          </div>
          <TextLines lines={4} lh={28} />
        </div>
      ))}
    </Card>
  </div>
);

/** SEO box: breadcrumb line on the page + white rounded text card with a «more» button. */
const SeoSkeleton = () => (
  <div className="flex flex-col gap-2 h-full">
    <div className="flex items-center gap-3 h-[21px]">
      <B w="61px" h={21} />
      <B w="177px" h={21} />
    </div>
    <div className="bg-white rounded-lg p-4 flex flex-col gap-3 flex-grow">
      <TextLines lines={4} />
      <B w="138px" h={33} className="self-end" />
    </div>
  </div>
);

/** Gallery: title on the page, white card with a row of 120×170 thumbnails. */
const GallerySkeleton = () => (
  <div className="flex flex-col gap-3 h-full">
    <OuterTitle w="66px" />
    <Card className="pt-4 px-4 pb-3 flex gap-3 flex-grow">
      {[0, 1, 2].map(i => (
        <Skeleton key={i} w="120px" h="170px" className="rounded-md shrink-0" />
      ))}
    </Card>
  </div>
);

/** Generic text card (about, waiting chart): title + lines. */
const TextCardSkeleton = ({ lines }: { lines: number }) => (
  <Card className="p-4 flex flex-col gap-3 h-full">
    <B w="40%" h={20} />
    <TextLines lines={lines} />
  </Card>
);

const BODY: Partial<Record<SectionId, () => ReactElement>> = {
  head: HeadSkeleton,
  booking: BookingSkeleton,
  risman: BookingSkeleton,
  centers: CentersSkeleton,
  reviews: ReviewsSkeleton,
  seo: SeoSkeleton,
  gallery: GallerySkeleton,
  about: () => <TextCardSkeleton lines={130} />,
  waiting: () => <TextCardSkeleton lines={8} />,
};

export const ProfileSectionSkeleton = ({ id }: { id: SectionId }) => {
  const Body = BODY[id] ?? (() => <TextCardSkeleton lines={2} />);
  const hints = useContext(BookingHintsContext);
  const height = id === 'booking' ? bookingSkeletonHeight(hints) : SECTION_SKELETON_HEIGHT[id];
  return (
    <div className="overflow-hidden" style={{ height }} aria-busy="true">
      <Body />
    </div>
  );
};
